import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, ShieldCheck, ShieldAlert, ShieldX, ShieldOff,
  Lock, Unlock, Globe, Server, FileCheck, Key, Ban,
  AlertTriangle, CheckCircle, XCircle, RefreshCw,
  Clock, MapPin, Monitor, User, Activity, Database,
  Trash2, Eye, TrendingUp, TrendingDown
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SecurityDashboard = ({ token }) => {
  const [securityData, setSecurityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchSecurityData = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/security/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSecurityData(data.data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching security data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSecurityData();
    
    // Auto-refresh every 30 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchSecurityData, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchSecurityData, autoRefresh]);

  const handleUnblockIP = async (ip) => {
    if (!window.confirm(`Desbloquear o IP ${ip}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/security/unblock-ip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ip })
      });
      
      if (response.ok) {
        fetchSecurityData();
      }
    } catch (error) {
      console.error('Error unblocking IP:', error);
    }
  };

  const handleClearAlerts = async () => {
    if (!window.confirm('Limpar todos os alertas de segurança?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/security/clear-alerts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSecurityData();
      }
    } catch (error) {
      console.error('Error clearing alerts:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('pt-BR', { 
      day: '2-digit', month: '2-digit', 
      hour: '2-digit', minute: '2-digit' 
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-400 bg-red-500/20 border-red-500/30';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'low': return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return severity;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!securityData) {
    return (
      <div className="text-center text-gray-400 py-8">
        Erro ao carregar dados de segurança
      </div>
    );
  }

  const { protections, login_chart_data, blocked_ips, recent_logins, security_alerts } = securityData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-emerald-500" />
          <h2 className="text-xl font-bold text-white">Dashboard de Segurança</h2>
          {securityData.all_protections_active && (
            <span className="px-2 py-1 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Todas proteções ativas
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input 
              type="checkbox" 
              checked={autoRefresh} 
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-emerald-500 focus:ring-emerald-500"
            />
            Auto-refresh (30s)
          </label>
          <button 
            onClick={fetchSecurityData}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
          {lastUpdate && (
            <span className="text-xs text-gray-500">
              Atualizado: {lastUpdate.toLocaleTimeString('pt-BR')}
            </span>
          )}
        </div>
      </div>

      {/* Protection Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {Object.entries(protections).map(([key, protection]) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-3 rounded-lg border ${
              protection.active 
                ? 'bg-emerald-500/10 border-emerald-500/30' 
                : 'bg-red-500/10 border-red-500/30'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              {protection.active ? (
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
              ) : (
                <ShieldX className="w-4 h-4 text-red-500" />
              )}
              <span className={`text-xs font-medium ${protection.active ? 'text-emerald-400' : 'text-red-400'}`}>
                {protection.active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <h3 className="text-sm font-medium text-white truncate">{protection.name}</h3>
            <p className="text-xs text-gray-400 mt-1 truncate">{protection.description}</p>
            {protection.blocked_today !== undefined && (
              <p className="text-xs text-yellow-400 mt-1">{protection.blocked_today} bloqueados hoje</p>
            )}
            {protection.blocked_count !== undefined && (
              <p className="text-xs text-red-400 mt-1">{protection.blocked_count} IPs bloqueados</p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Ban className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{securityData.requests_blocked_today || 0}</p>
              <p className="text-xs text-gray-400">Requests bloqueados hoje</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Globe className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{securityData.unique_ips_today || 0}</p>
              <p className="text-xs text-gray-400">IPs únicos hoje</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Activity className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{securityData.uptime || '0m'}</p>
              <p className="text-xs text-gray-400">Uptime do sistema</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Database className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white truncate">
                {securityData.last_backup ? new Date(securityData.last_backup).toLocaleDateString('pt-BR') : 'Nunca'}
              </p>
              <p className="text-xs text-gray-400">Último backup</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Login Activity Chart */}
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            Atividade de Login (últimas 24h)
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={login_chart_data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="hour" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Bar dataKey="success" name="Sucesso" fill="#10B981" radius={[2, 2, 0, 0]} />
                <Bar dataKey="failed" name="Falha" fill="#EF4444" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-2 text-sm">
            <span className="flex items-center gap-2 text-emerald-400">
              <CheckCircle className="w-4 h-4" />
              {securityData.successful_logins_24h || 0} bem-sucedidos
            </span>
            <span className="flex items-center gap-2 text-red-400">
              <XCircle className="w-4 h-4" />
              {securityData.blocked_attempts_24h || 0} falhos
            </span>
          </div>
        </div>

        {/* Blocked IPs Table */}
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-500" />
            IPs Bloqueados ({blocked_ips?.length || 0})
          </h3>
          <div className="overflow-auto max-h-48">
            {blocked_ips && blocked_ips.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700">
                    <th className="pb-2">IP</th>
                    <th className="pb-2">Local</th>
                    <th className="pb-2">Motivo</th>
                    <th className="pb-2">Tentativas</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {blocked_ips.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-700/50">
                      <td className="py-2 text-white font-mono text-xs">{item.ip}</td>
                      <td className="py-2 text-gray-400 text-xs">
                        {item.city}, {item.country}
                      </td>
                      <td className="py-2 text-red-400 text-xs truncate max-w-[150px]" title={item.reason}>
                        {item.reason}
                      </td>
                      <td className="py-2 text-yellow-400 text-xs">{item.attempts}</td>
                      <td className="py-2">
                        <button
                          onClick={() => handleUnblockIP(item.ip)}
                          className="p-1 text-gray-400 hover:text-emerald-400 transition-colors"
                          title="Desbloquear"
                        >
                          <Unlock className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                Nenhum IP bloqueado
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Logins & Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Logins */}
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-blue-500" />
            Últimas 10 Tentativas de Login
          </h3>
          <div className="overflow-auto max-h-64">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="pb-2">Horário</th>
                  <th className="pb-2">IP</th>
                  <th className="pb-2">Local</th>
                  <th className="pb-2">Usuário</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent_logins && recent_logins.slice(0, 10).map((login, idx) => (
                  <tr 
                    key={idx} 
                    className={`border-b border-gray-700/50 ${!login.success ? 'bg-red-500/5' : ''}`}
                  >
                    <td className="py-2 text-gray-400 text-xs">
                      {formatTimestamp(login.timestamp)}
                    </td>
                    <td className="py-2 text-white font-mono text-xs">{login.ip}</td>
                    <td className="py-2 text-gray-400 text-xs">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {login.city || 'Desconhecido'}
                      </div>
                    </td>
                    <td className="py-2 text-white text-xs">{login.username || '-'}</td>
                    <td className="py-2">
                      {login.success ? (
                        <span className="flex items-center gap-1 text-emerald-400 text-xs">
                          <CheckCircle className="w-3 h-3" /> Sucesso
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-xs">
                          <XCircle className="w-3 h-3" /> Falha
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!recent_logins || recent_logins.length === 0) && (
              <div className="text-center text-gray-500 py-8">
                Nenhuma tentativa de login registrada
              </div>
            )}
          </div>
        </div>

        {/* Security Alerts */}
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              Alertas de Segurança ({securityData.alerts_count || 0})
            </h3>
            {security_alerts && security_alerts.length > 0 && (
              <button
                onClick={handleClearAlerts}
                className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
                Limpar
              </button>
            )}
          </div>
          <div className="overflow-auto max-h-64 space-y-2">
            <AnimatePresence>
              {security_alerts && security_alerts.length > 0 ? (
                security_alerts.map((alert, idx) => (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 text-xs rounded font-medium ${getSeverityColor(alert.severity)}`}>
                            {getSeverityLabel(alert.severity)}
                          </span>
                          <span className="text-white text-sm font-medium">{alert.title}</span>
                        </div>
                        <p className="text-xs text-gray-400">{alert.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimestamp(alert.timestamp)}
                          </span>
                          {alert.ip && (
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {alert.ip}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                  Nenhum alerta nas últimas 24h
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard;
